import React, { useState } from "react";
import { CardComp, SelectComp2, ButtonComp, DropDownComp } from "../../../components";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { TableCompData } from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import TablePagination from "../../../components/Table/Pagination";
import {
  useGetBranchSmsHistoryQuery,
  useGetSmsStatisticsQuery,
} from "../../../store/admin/smsHistorySlice";
import SearchBar from "../../Members/modules/SearchBar";
import { FiDownload } from "react-icons/fi";
import { storage } from "../../../utils/storage";
import { BsChevronDown, BsThreeDotsVertical } from "react-icons/bs";
import moment from "moment";
import {
  formatSourceType,
  getSourceTypeColor,
  maskPhoneNumber,
  getStatusBadgeColor,
  getStatusBadgeText,
} from "../../../utils/smsHistoryUtils";
import SmsHistoryDetailsModal from "./modals/SmsHistoryDetailsModal";

export default function SmsHistory() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sourceType, setSourceType] = useState("all");
  const [status, setStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: smsHistoryData,
    isFetching,
    isLoading,
  } = useGetBranchSmsHistoryQuery({
    page,
    limit: 20,
    date_from: fromDate || undefined,
    date_to: toDate || undefined,
    source_type: sourceType === "all" ? undefined : sourceType,
    status: status === "all" ? undefined : status,
    recipient_phone: search || undefined,
  });

  const {
    data: statisticsData,
  } = useGetSmsStatisticsQuery({
    date_from: fromDate || undefined,
    date_to: toDate || undefined,
  });

  // Extract logs array - PublicCollection returns { logs: [...], pagination: {...} }
  let logs = [];
  if (Array.isArray(smsHistoryData?.logs)) {
    logs = smsHistoryData.logs;
  } else if (smsHistoryData?.logs?.logs && Array.isArray(smsHistoryData.logs.logs)) {
    logs = smsHistoryData.logs.logs;
  } else if (smsHistoryData?.logs?.data && Array.isArray(smsHistoryData.logs.data)) {
    logs = smsHistoryData.logs.data;
  }
  
  // Extract pagination - controller provides it at root level, but PublicCollection also has it
  const pagination = smsHistoryData?.pagination || smsHistoryData?.logs?.pagination || {};
  const summary = smsHistoryData?.summary || {};
  const statistics = statisticsData || {};

  const sourceTypeOptions = [
    { label: "All Types", value: "all" },
    { label: "Event", value: "event" },
    { label: "Communication Message", value: "communication_message" },
    { label: "Birthday", value: "birthday" },
    { label: "E-Minute", value: "eminute" },
    { label: "Other", value: "other" },
  ];

  const statusOptions = [
    { label: "All Statuses", value: "all" },
    { label: "Sent", value: "sent" },
    { label: "Failed", value: "failed" },
    { label: "Delivered", value: "delivered" },
    { label: "Pending", value: "pending" },
  ];

  const TableDropDown = (row) => [
    {
      name: "View Details",
      action: (data) => {
        setSelectedLogId(data?.id);
        setIsModalOpen(true);
      },
    },
  ];

  const handleDownloadReport = (format = "excel") => {
    const params = new URLSearchParams({
      guard: "staff",
      format: format,
      source_type: sourceType === "all" ? "" : sourceType,
      status: status === "all" ? "" : status,
      date_from: fromDate || "",
      date_to: toDate || "",
      recipient_phone: search || "",
    });

    const token = storage["cookieStorage"].get("access_token");
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const url = `${baseUrl}/cooperatives/reports/sms-history/export?${params.toString()}`;

    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to download report");
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);

        if (format === "pdf") {
          const newWindow = window.open(blobUrl, "_blank");
          if (!newWindow) {
            alert("Please allow popups to view the printable report");
          }
        } else {
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = `SMS_History_Report_${new Date().toISOString().split("T")[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      })
      .catch((err) => {
        console.error("Download failed", err);
        alert("Failed to download report. Please try again.");
      });
  };

  const downloadOptions = [
    {
      name: "Download as Excel (CSV)",
      action: () => handleDownloadReport("excel"),
    },
    {
      name: "Download as PDF (Printable)",
      action: () => handleDownloadReport("pdf"),
    },
  ];

  // Statistics Cards Data
  const totalSent = summary?.total_sent || statistics?.total_sent || 0;
  const totalFailed = summary?.total_failed || statistics?.total_failed || 0;
  const totalDelivered = statistics?.total_delivered || 0;
  const totalCost = summary?.total_cost || statistics?.total_cost || 0;

  // Table Columns
  const columns = [
    {
      name: "Recipient Name",
      selector: (row) => row?.recipient_name || "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: "Recipient Phone",
      selector: (row) => maskPhoneNumber(row?.recipient_phone),
      sortable: true,
      width: "130px",
    },
    {
      name: "Source Type",
      selector: (row) => (
        <span
          className="px-2 py-1 rounded-pill text-white fw-semibold"
          style={{
            backgroundColor: getSourceTypeColor(row?.source_type),
            fontSize: "11px",
            minWidth: "fit-content",
            whiteSpace: "nowrap",
          }}
        >
          {formatSourceType(row?.source_type)}
        </span>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "Source Name",
      selector: (row) => row?.source_name || "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: "Message Preview",
      selector: (row) => {
        const message = row?.message || "";
        return message.length > 50 ? `${message.substring(0, 50)}...` : message;
      },
      sortable: false,
      width: "200px",
    },
    {
      name: "SMS Count",
      selector: (row) => row?.sms_count || 1,
      sortable: true,
      width: "100px",
      center: true,
    },
    {
      name: "Status",
      selector: (row) => (
        <span
          className="px-2 py-1 rounded-pill text-white fw-semibold"
          style={{
            backgroundColor: getStatusBadgeColor(row?.status),
            fontSize: "11px",
            minWidth: "fit-content",
            whiteSpace: "nowrap",
          }}
        >
          {getStatusBadgeText(row?.status)}
        </span>
      ),
      sortable: true,
      width: "100px",
      center: true,
    },
    {
      name: "Sent At",
      selector: (row) =>
        row?.sent_at
          ? moment(row.sent_at).format("DD-MMM-YYYY HH:mm")
          : "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: "Sent By",
      selector: (row) => row?.sent_by?.name || "N/A",
      sortable: true,
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="text-center">
          <DropDownComp
            dropLabelClassName="bg-white border-0 px-3 py-1 rounded-4"
            arrayComp={TableDropDown(row)}
            ItemClassName="pe-5"
            input="name"
            row={row}
            DropDownText={
              <span>
                <BsThreeDotsVertical size={20} className="table-action-icon" />
              </span>
            }
          />
        </div>
      ),
      width: "100px",
      center: true,
      sortable: false,
    },
  ];

  return (
    <div className="p-4">
      <div className="row align-items-center mb-4 g-3">
        <div className="col-md">
          <h4 className="fw-bold mb-0">SMS History</h4>
          <p className="text-muted small mb-0">
            View and track all SMS communications sent from your branch
          </p>
        </div>
        <div className="col-md-auto">
          <DropDownComp
            dropLabelClassName="bg-primary1 text-white px-4 py-2 rounded shadow-sm border-0 d-flex align-items-center gap-2"
            arrayComp={downloadOptions}
            input="name"
            DropDownText={
              <div className="d-flex align-items-center gap-2">
                <FiDownload />
                <span className="fw-bold">Download Report</span>
                <BsChevronDown size={14} className="ms-1" />
              </div>
            }
            style={{ minWidth: "220px" }}
          />
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3 col-6">
          <CardComp cardClassName="border-0 shadow-sm h-100">
            <div className="p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <i
                  className="fas fa-sms"
                  style={{ color: "var(--color-secondary)", fontSize: "20px" }}
                ></i>
                <span
                  className="px-2 py-1 rounded-pill text-white fw-semibold"
                  style={{
                    backgroundColor: "var(--color-secondary)",
                    fontSize: "11px",
                    minWidth: "fit-content",
                  }}
                >
                  {totalSent}
                </span>
              </div>
              <p className="text-muted mb-0 small" style={{ fontSize: "12px" }}>
                Total Sent
              </p>
            </div>
          </CardComp>
        </div>
        <div className="col-md-3 col-6">
          <CardComp cardClassName="border-0 shadow-sm h-100">
            <div className="p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <i
                  className="fas fa-check-circle"
                  style={{ color: "#19BC31", fontSize: "20px" }}
                ></i>
                <span
                  className="px-2 py-1 rounded-pill text-white fw-semibold"
                  style={{
                    backgroundColor: "#19BC31",
                    fontSize: "11px",
                    minWidth: "fit-content",
                  }}
                >
                  {totalSent - totalFailed}
                </span>
              </div>
              <p className="text-muted mb-0 small" style={{ fontSize: "12px" }}>
                Successfully Sent
              </p>
            </div>
          </CardComp>
        </div>
        <div className="col-md-3 col-6">
          <CardComp cardClassName="border-0 shadow-sm h-100">
            <div className="p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <i
                  className="fas fa-exclamation-circle"
                  style={{
                    color: totalFailed > 0 ? "#FF4343" : "#A1A0A0",
                    fontSize: "20px",
                  }}
                ></i>
                <span
                  className="px-2 py-1 rounded-pill text-white fw-semibold"
                  style={{
                    backgroundColor: totalFailed > 0 ? "#FF4343" : "#A1A0A0",
                    fontSize: "11px",
                    minWidth: "fit-content",
                  }}
                >
                  {totalFailed}
                </span>
              </div>
              <p className="text-muted mb-0 small" style={{ fontSize: "12px" }}>
                Failed
              </p>
            </div>
          </CardComp>
        </div>
        <div className="col-md-3 col-6">
          <CardComp cardClassName="border-0 shadow-sm h-100">
            <div className="p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <i
                  className="fas fa-coins"
                  style={{ color: "var(--color-primary)", fontSize: "20px" }}
                ></i>
                <span
                  className="px-2 py-1 rounded-pill text-white fw-semibold"
                  style={{
                    backgroundColor: "var(--color-primary)",
                    fontSize: "11px",
                    minWidth: "fit-content",
                  }}
                >
                  {totalCost}
                </span>
              </div>
              <p className="text-muted mb-0 small" style={{ fontSize: "12px" }}>
                Total Cost
              </p>
            </div>
          </CardComp>
        </div>
      </div>

      {/* Filters Section */}
      <CardComp cardClassName="border-0 shadow-sm mb-4" data-tour="staff-reports-filters">
        <div className="p-3">
          <div className="row g-3 align-items-end">
            <div className="col-lg-4 col-md-12">
              <label className="form-label small fw-bold mb-2">
                Search by Phone Number
              </label>
              <div className="sms-history-search-wrapper">
                <SearchBar
                  placeholder="Search by phone number..."
                  value={search}
                  onSearch={(val) => {
                    setSearch(val);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-lg-2 col-md-6">
              <SelectComp2
                LabelText="Source Type"
                selectedOption={sourceType}
                setSelectedOption={(opt) => {
                  setSourceType(opt?.value || "all");
                  setPage(1);
                }}
                arrayComp={sourceTypeOptions}
                LabelClassName="fw-bold small mb-2"
                selectClassName="w-100 py-2 rounded border"
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <SelectComp2
                LabelText="Status"
                selectedOption={status}
                setSelectedOption={(opt) => {
                  setStatus(opt?.value || "all");
                  setPage(1);
                }}
                arrayComp={statusOptions}
                LabelClassName="fw-bold small mb-2"
                selectClassName="w-100 py-2 rounded border"
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <div className="d-flex flex-column h-100">
                <label className="form-label small fw-bold mb-2">From Date</label>
                <input
                  type="date"
                  className="form-control py-2 rounded border shadow-none"
                  style={{ height: "42px", backgroundColor: "#fafafa" }}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-lg-2 col-md-6">
              <div className="d-flex flex-column h-100">
                <label className="form-label small fw-bold mb-2">To Date</label>
                <input
                  type="date"
                  className="form-control py-2 rounded border shadow-none"
                  style={{ height: "42px", backgroundColor: "#fafafa" }}
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </CardComp>

      <style>{`
        .sms-history-search-wrapper .input-group.search {
          margin-bottom: 0 !important;
        }
        .sms-history-search-wrapper .input-group-text {
          border-top-left-radius: 8px !important;
          border-bottom-left-radius: 8px !important;
          height: 42px;
        }
        .sms-history-search-wrapper .form-control {
          border-top-right-radius: 8px !important;
          border-bottom-right-radius: 8px !important;
          height: 42px;
          border: 1px solid #ced4da !important;
        }
      `}</style>

      {/* Table */}
      <CardComp cardClassName="border-0 shadow-sm" data-tour="staff-reports-table">
        <TableCompData
          marginBottom={"1rem"}
          columns={columns}
          data={Array.isArray(logs) ? logs : []}
          progressPending={isLoading || isFetching}
          progressComponent={<TableSkeletonLoader rows={10} columns={10} />}
          message={
            !isLoading &&
            !isFetching &&
            (!Array.isArray(logs) || logs.length === 0) && (
              <EmptyTable Message="No SMS history found matching your criteria." />
            )
          }
        />
        {!isLoading && !isFetching && Array.isArray(logs) && logs.length > 0 && (
          <div className="p-3">
            <TablePagination
              handlePageChange={setPage}
              currentPage={pagination.current_page || pagination.currentPage || 1}
              totalPages={pagination.last_page || pagination.totalPages || 1}
              total={pagination.total || 0}
              perPage={pagination.per_page || pagination.perPage || 20}
              count={pagination.count || logs.length}
            />
          </div>
        )}
      </CardComp>

      {isModalOpen && selectedLogId && (
        <SmsHistoryDetailsModal
          logId={selectedLogId}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}
